/* eslint-disable */
import styles from './index.module.scss'
import Useful from '../../components/pages/useful'
import translate from '../../utils/translate.json'
import { t, calc3Dict, calc3Ext, calcCommon } from '../../utils/calculatorTranslations'
import { PageContext } from '../../context/PageContextProvider'
import { useState, useContext, useEffect, useMemo } from 'react'
import { Calculator, Info } from '../../icons'
import PagesAPI from '../../api/pages'
import Loading from '../../components/pages/loading'
import StepLine from '../../components/core/stepLine'
import {
  calculateMaternityBenefit2026,
  getLeaveDurationDays,
  calculateLeaveDates,
  getPrecedingFourQuarters,
  calculateExcludedDays,
  MAX_MONTHLY_LIMIT_2026,
} from './maternityCalculator2026'

const getTodayString = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const Calculator3 = ({ className }) => {
  const { language } = useContext(PageContext)
  const [load, setLoad] = useState(false)
  const [info, setInfo] = useState([])

  // State
  const [startDate, setStartDate] = useState(getTodayString())
  const [activityType, setActivityType] = useState('general') // 'general' | 'agriculture'
  const [birthType, setBirthType] = useState('normal') // 'normal' | 'complicated' | 'multiple'
  const [enableActualBirth, setEnableActualBirth] = useState(false)
  const [actualBirthDate, setActualBirthDate] = useState('')
  const [seniorityMonths, setSeniorityMonths] = useState(12)
  const [hadEmployment, setHadEmployment] = useState(true)

  // Input mode: 'MDSS' | 'QAZANC'
  const [inputMode, setInputMode] = useState('MDSS')

  // Quarterly inputs
  const [quarterlyMDSS, setQuarterlyMDSS] = useState({})
  const [quarterlyEarnings, setQuarterlyEarnings] = useState({})

  const [excludedPeriods, setExcludedPeriods] = useState([])
  const [showExcluded, setShowExcluded] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const data1 = await PagesAPI.getMagazinePageInfo()
        setInfo(data1?.data || [])
      } catch (e) {}
      setLoad(true)
    })()
  }, [])

  const steps = [
    {
      title: translate.calculation_type,
      text: translate.make_your_choice,
    },
    {
      title: translate.basic_information,
      text: translate.enter_info,
    },
    {
      title: translate.result,
      text: translate.ready,
    },
  ]

  // Dynamic calculations
  const standardDays = useMemo(
    () => getLeaveDurationDays(activityType, birthType),
    [activityType, birthType]
  )

  const dateInfo = useMemo(
    () => calculateLeaveDates(startDate, standardDays, enableActualBirth ? actualBirthDate : null),
    [startDate, standardDays, enableActualBirth, actualBirthDate]
  )

  const quarterInfo = useMemo(
    () => getPrecedingFourQuarters(startDate),
    [startDate]
  )

  const excludedInfo = useMemo(
    () => calculateExcludedDays(excludedPeriods, quarterInfo.basePeriodStart, quarterInfo.basePeriodEnd),
    [excludedPeriods, quarterInfo]
  )

  // Handlers for quarterly inputs with automatic sync
  const handleMDSSChange = (key, val) => {
    setQuarterlyMDSS((prev) => ({ ...prev, [key]: val }))
    const num = parseFloat(val) || 0
    setQuarterlyEarnings((prev) => ({
      ...prev,
      [key]: val === '' ? '' : (num * 4).toFixed(2),
    }))
  }

  const handleEarningChange = (key, val) => {
    setQuarterlyEarnings((prev) => ({ ...prev, [key]: val }))
    const num = parseFloat(val) || 0
    setQuarterlyMDSS((prev) => ({
      ...prev,
      [key]: val === '' ? '' : (num / 4).toFixed(2),
    }))
  }

  const addExcludedPeriod = () => {
    setExcludedPeriods((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'xestelik',
        startDate: quarterInfo.basePeriodStart || '',
        endDate: quarterInfo.basePeriodStart || '',
      },
    ])
  }

  const removeExcludedPeriod = (id) => {
    setExcludedPeriods((prev) => prev.filter((item) => item.id !== id))
  }

  const updateExcludedPeriod = (id, field, value) => {
    setExcludedPeriods((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    )
  }

  const handleCalculate = () => {
    const result = calculateMaternityBenefit2026({
      startDate,
      actualBirthDate: enableActualBirth ? actualBirthDate : null,
      activityType,
      birthType,
      seniorityMonths,
      hadEmployment,
      quarterlyMDSS,
      excludedPeriods,
    })

    // Store result in sessionStorage for Result3 page
    try {
      sessionStorage.setItem('maternity_result_2026', JSON.stringify({
        ...result,
        quarterlyMDSS,
        quarterlyEarnings,
        inputMode,
      }))
    } catch (e) {
      console.error(e)
    }

    // Build URL parameters for fallback and navigate
    const sDate = result.startDate
    const eDate = result.endDate
    const tgs = result.TGS
    const btg = result.BTG
    const uqm = result.ÜQM.toFixed(2)
    const oqm = result.OQM.toFixed(2)
    const finalBenefit = result.finalBenefit.toFixed(2)

    window.location.href = `/useful/calculator/3/result/${sDate}/${eDate}/${tgs}/${btg}/${uqm}/${oqm}/${tgs}/${finalBenefit}`
  }

  return load ? (
    <section className={`${styles.container} ${className || ''}`}>
      <Useful search={false} />
      <div className={styles.stepline}>
        <StepLine active={1} data={steps} />
      </div>

      <h1 className={styles.pageTitle}>
        <Calculator />
        {t(calc3Dict.title, language)}
      </h1>

      <div className={styles.formCard}>
        {/* Section 1: Məzuniyyət məlumatları */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>1</div>
          <div>
            <h3>{t(calc3Dict.sec1Title, language)}</h3>
            <p>{t(calc3Dict.sec1Sub, language)}</p>
          </div>
        </div>

        <div className={styles.grid3}>
          <div className={styles.formGroup}>
            <label>{t(calc3Dict.startDate, language)}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span className={styles.hint}>{t(calc3Dict.startDateHint, language)}</span>
          </div>

          <div className={styles.formGroup}>
            <label>{t(calc3Dict.activityType, language)}</label>
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
            >
              <option value="general">{t(calc3Dict.actGeneral, language)}</option>
              <option value="agriculture">{t(calc3Dict.actAgriculture, language)}</option>
            </select>
            <span className={styles.hint}>{language === 'TR' ? 'Tarımsal üretimde izin süresi daha uzundur' : language === 'ENG' ? 'Leave duration is longer in agriculture' : language === 'RU' ? 'В сельском хозяйстве срок отпуска длиннее' : 'Kənd təsərrüfatında müddət daha uzundur'}</span>
          </div>

          <div className={styles.formGroup}>
            <label>{t(calc3Dict.birthType, language)}</label>
            <select
              value={birthType}
              onChange={(e) => setBirthType(e.target.value)}
            >
              <option value="normal">{t(calc3Dict.birthNormal, language)} ({activityType === 'agriculture' ? (language === 'ENG' ? '140 days' : language === 'RU' ? '140 дней' : '140 gün') : (language === 'ENG' ? '126 days' : language === 'RU' ? '126 дней' : '126 gün')})</option>
              <option value="complicated">{t(calc3Dict.birthComplicated, language)} ({activityType === 'agriculture' ? (language === 'ENG' ? '156 days' : language === 'RU' ? '156 дней' : '156 gün') : (language === 'ENG' ? '140 days' : language === 'RU' ? '140 дней' : '140 gün')})</option>
              <option value="multiple">{t(calc3Dict.birthMultiple, language)} ({activityType === 'agriculture' ? (language === 'ENG' ? '180 days' : language === 'RU' ? '180 дней' : '180 gün') : (language === 'ENG' ? '140 days' : language === 'RU' ? '140 дней' : '140 gün')})</option>
            </select>
            <span className={styles.hint}>{t(calc3Ext.medHint, language)}</span>
          </div>
        </div>

        {/* Live Duration Preview */}
        {dateInfo && (
          <div className={styles.durationPreview}>
            <div className={styles.durationItem}>
              <span>{language === 'TR' ? 'İzin Süresi (TGS)' : language === 'ENG' ? 'Leave Duration (TGS)' : language === 'RU' ? 'Срок отпуска (TGS)' : 'Məzuniyyət Müddəti (TGS)'}</span>
              <strong>{dateInfo.totalDays} {t(calcCommon.days, language)}</strong>
            </div>
            <div className={styles.durationItem}>
              <span>{language === 'TR' ? 'Başlangıç Tarihi' : language === 'ENG' ? 'Start Date' : language === 'RU' ? 'Дата начала' : 'Başlama Tarixi'}</span>
              <strong>{dateInfo.startDate}</strong>
            </div>
            <div className={styles.durationItem}>
              <span>{language === 'TR' ? 'Bitiş Tarihi' : language === 'ENG' ? 'End Date' : language === 'RU' ? 'Дата окончания' : 'Bitmə Tarixi'}</span>
              <strong>{dateInfo.endDate}</strong>
            </div>
            <div className={styles.durationItem}>
              <span>{language === 'TR' ? 'Aylık Yasal Tavan (Madde 3.3)' : language === 'ENG' ? 'Monthly Legal Cap (Clause 3.3)' : language === 'RU' ? 'Месячный лимит (Пункт 3.3)' : 'Aylıq Qanuni Hədd (Bənd 3.3)'}</span>
              <strong>{MAX_MONTHLY_LIMIT_2026.toLocaleString('az-AZ')} ₼</strong>
            </div>
          </div>
        )}

        {/* Actual birth delay extension */}
        <div className={styles.formGroup}>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={enableActualBirth}
              onChange={(e) => setEnableActualBirth(e.target.checked)}
            />
            <span>{t(calc3Dict.enableActualBirth, language)}</span>
          </label>
          {enableActualBirth && (
            <div style={{ maxWidth: 320, marginTop: 8 }}>
              <input
                type="date"
                value={actualBirthDate}
                onChange={(e) => setActualBirthDate(e.target.value)}
              />
              <span className={styles.hint}>
                {t(calc3Ext.birthDelayHint, language)}
              </span>
            </div>
          )}
        </div>

        {/* Seniority */}
        <div className={styles.formGroup}>
          <label>{t(calc3Dict.seniority, language)}</label>
          <div style={{ maxWidth: 320 }}>
            <input
              type="number"
              min="0"
              value={seniorityMonths}
              onChange={(e) => setSeniorityMonths(e.target.value)}
            />
          </div>
          {Number(seniorityMonths) < 6 ? (
            <div className={styles.warningBox}>
              <Info />
              <div>
                <strong>⚠️ 6 aylıq sosial sığorta stajı şərti üzrə xəbərdarlıq:</strong> Sosial sığorta stajı 6 aydan azdır. Hesablanmış məbləğ yalnız riyazi göstəricidir. Müavinət alma hüququ və faktiki məbləğ DSMF/ƏMAS məlumatları və tətbiq olunan qanunvericilik əsasında müəyyən edilir.
              </div>
            </div>
          ) : (
            <div className={styles.infoBox}>
              <Info />
              <div>
                Sosial sığorta stajı şərti ödənilib ({seniorityMonths} ay).
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Əvvəlki 4 ardıcıl rüb */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>2</div>
          <div>
            <h3>{t(calc3Dict.sec2Title, language)}</h3>
            <p>
              Qaydanın 2.7-ci bəndinə əsasən, əsas hüquqi göstərici nəzərə alınan MDSS haqqıdır (Qazanc = MDSS × 4).
              İstifadəçi rahatlığı üçün rüblük qazancı da daxil edə bilərsiniz.
            </p>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>{t(calc3Dict.hadEmployment, language)}</label>
          <div className={styles.radioGroup}>
            <label className={`${styles.radioLabel} ${hadEmployment ? styles.active : ''}`}>
              <input
                type="radio"
                name="hadEmployment"
                checked={hadEmployment}
                onChange={() => setHadEmployment(true)}
              />
              {t(calc3Dict.yesEmployment, language)}
            </label>
            <label className={`${styles.radioLabel} ${!hadEmployment ? styles.active : ''}`}>
              <input
                type="radio"
                name="hadEmployment"
                checked={!hadEmployment}
                onChange={() => setHadEmployment(false)}
              />
              {t(calc3Dict.noEmployment, language)}
            </label>
          </div>
        </div>

        {hadEmployment ? (
          <div>
            {/* Input mode selection */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{t(calc3Dict.inputMode, language)}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="inputMode"
                  checked={inputMode === 'MDSS'}
                  onChange={() => setInputMode('MDSS')}
                />
                {t(calc3Dict.modeMDSS, language)}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="inputMode"
                  checked={inputMode === 'QAZANC'}
                  onChange={() => setInputMode('QAZANC')}
                />
                {t(calc3Dict.modeEarnings, language)}
              </label>
            </div>

            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              {t(calc3Ext.basePeriod, language)} <strong>{quarterInfo.basePeriodStart} – {quarterInfo.basePeriodEnd}</strong> ({t(calc3Ext.actualCalDays, language)} <strong>{quarterInfo.totalCalendarDays} gün</strong>).
            </p>

            <div className={styles.grid4}>
              {quarterInfo.quarters.map((q) => {
                const mdssVal = quarterlyMDSS[q.key] !== undefined ? quarterlyMDSS[q.key] : ''
                const earnVal = quarterlyEarnings[q.key] !== undefined ? quarterlyEarnings[q.key] : ''
                return (
                  <div key={q.key} className={styles.quarterCard}>
                    <div className={styles.qHeader}>
                      <h4>{q.label}</h4>
                      <span className={styles.daysBadge}>{q.calendarDays} gün</span>
                    </div>
                    <div className={styles.qDates}>
                      {q.startDate} – {q.endDate}
                    </div>

                    {inputMode === 'MDSS' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Nəzərə alınan MDSS haqqı (₼):</span>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="Məs: 750"
                          className={styles.qInput}
                          value={mdssVal}
                          onChange={(e) => handleMDSSChange(q.key, e.target.value)}
                        />
                        <div className={styles.qEarningPreview}>
                          Nəzərə alınan qazanc: {earnVal ? `${earnVal} ₼` : '0.00 ₼'} (MDSS × 4)
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Rüblük Qazanc / Maaş (₼):</span>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="Məs: 3000"
                          className={styles.qInput}
                          value={earnVal}
                          onChange={(e) => handleEarningChange(q.key, e.target.value)}
                        />
                        <div className={styles.qEarningPreview}>
                          Hesablama üçün MDSS ekvivalenti: {mdssVal ? `${mdssVal} ₼` : '0.00 ₼'} (Qazanc ÷ 4)
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 10 }}>
              * Qeyd: Faktiki sosial sığorta məlumatları DSMF-nin elektron informasiya sistemindəki məlumatlar əsasında müəyyən edilir.
            </p>
          </div>
        ) : (
          <div className={styles.infoBox}>
            <Info />
            <div>
              <strong>{t(calc3Ext.clause2_9Title, language)}</strong> Əvvəlki 4 ardıcıl rübdə əmək
              fəaliyyəti olmadıqda, orta gündəlik qazanc hadisənin baş verdiyi aydakı minimum aylıq
              əməkhaqqının (2026-cı il üzrə <strong>400 AZN</strong>) həmin aydakı təqvim günlərinin
              sayına bölünməsi yolu ilə hesablanır.
            </div>
          </div>
        )}

        {/* Section 3: Çıxılan təqvim günləri (Bənd 2.13) */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>3</div>
          <div>
            <h3>{t(calc3Dict.sec3Title, language)}</h3>
            <p>
              Yalnız normativ hüquqi aktlarla müəyyən edilmiş dövrlər bölən təqvim günlərindən (BTG) çıxılır.
            </p>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={showExcluded}
              onChange={(e) => setShowExcluded(e.target.checked)}
            />
            <span>Əvvəlki 4 rüb ərzində çıxılan dövrlərim olub (+{excludedInfo.excludedDays} gün baza dövrü ilə kəsişir)</span>
          </label>
        </div>

        {showExcluded && (
          <div className={styles.excludedPanel}>
            <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>
              Daxil edilən dövrün yalnız 4 tam rüblük baza dövrünə ({quarterInfo.basePeriodStart} – {quarterInfo.basePeriodEnd}) düşən hissəsi ÇTG-yə daxil edilir.
            </p>
            {excludedPeriods.map((period) => (
              <div key={period.id} className={styles.excludedRow}>
                <select
                  value={period.type}
                  onChange={(e) => updateExcludedPeriod(period.id, 'type', e.target.value)}
                >
                  <option value="xestelik">{t(calc3Ext.exclIllness, language)}</option>
                  <option value="qulluq">{t(calc3Ext.exclCare, language)}</option>
                  <option value="hamilelik">{t(calc3Ext.exclPregnancy, language)}</option>
                  <option value="odenissiz">{t(calc3Ext.exclUnpaid, language)}</option>
                  <option value="kenarlasdirma">{t(calc3Ext.exclSuspension, language)}</option>
                </select>
                <input
                  type="date"
                  value={period.startDate}
                  onChange={(e) => updateExcludedPeriod(period.id, 'startDate', e.target.value)}
                />
                <span>–</span>
                <input
                  type="date"
                  value={period.endDate}
                  onChange={(e) => updateExcludedPeriod(period.id, 'endDate', e.target.value)}
                />
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeExcludedPeriod(period.id)}
                >
                  Sil
                </button>
              </div>
            ))}
            <button
              type="button"
              className={styles.addBtn}
              onClick={addExcludedPeriod}
            >
              {t(calc3Dict.addPeriodBtn, language)}
            </button>
          </div>
        )}

        {/* Hesabla Button */}
        <div className={styles.calcActions}>
          <button type="button" onClick={handleCalculate}>
            {t(calcCommon.calculateBtn, language).toUpperCase()}
          </button>
        </div>
      </div>

      {/* 2026 Hüquqi izah bölməsi */}
      <section className={styles.legalExplanation}>
        <div className={styles.header}>
          <Info />
          <h3>2026-cı il Rəsmi Qanunvericilik və DSMF Hesablama Metodikası</h3>
        </div>
        <div className={styles.articleList}>
          <div className={styles.articleItem}>
            <h4>1. Əsas Hesablama Düsturu (Bənd 2.8 və 3.4)</h4>
            <p>
              Hamiləliyə və doğuma görə müavinət məzuniyyətin təqvim günlərinin sayının (TGS) bir
              təqvim gününə düşən orta gündəlik qazanca (OQM) vurulması yolu ilə müəyyən edilir:
            </p>
            <div className={styles.formulaBadge}>MM = TGS × OQM</div>
          </div>

          <div className={styles.articleItem}>
            <h4>2. Orta Gündəlik Qazancın Müəyyən Edilməsi (Bənd 2.7 və 2.8)</h4>
            <p>
              Orta gündəlik qazanc hadisənin baş verdiyi aydan əvvəlki ardıcıl 4 tam rüb üzrə
              hesablanmış ümumi qazancın (ÜQM) bölən təqvim günlərinin sayına (BTG) bölünməsi ilə
              tapılır. Qaydanın 2.7-ci bəndinə əsasən, qazanc sığortaolunan üçün ödənilmiş (həmin dövr
              üçün hesablanmış) məcburi dövlət sosial sığorta haqqının 4 misli kimi qəbul edilir:
            </p>
            <div className={styles.formulaBadge}>OQM = ÜQM / BTG &nbsp;|&nbsp; ÜQM = MDSS × 4 &nbsp;|&nbsp; BTG = ÜTG − ÇTG</div>
          </div>

          <div className={styles.articleItem}>
            <h4>3. Aylıq Maksimal Hədd (Bənd 3.3)</h4>
            <p>
              Nazirlər Kabinetinin 189 nömrəli Qərarının 3.3-cü bəndinə əsasən, hamiləliyə və doğuma
              görə müavinətin aylıq məbləği minimum əmək pensiyasının 25 mislindən artıq ola bilməz.
              2026-cı il üzrə minimum pensiya 320 AZN olduğundan, aylıq maksimal məbləğ{' '}
              <strong>8 000 AZN</strong> (320 × 25) təşkil edir.
            </p>
          </div>

          <div className={styles.articleItem}>
            <h4>4. Vergidən Azadolma (AR Vergi Məcəlləsi, Maddə 102.1.4)</h4>
            <p>
              AR Vergi Məcəlləsinin 102.1.4-cü maddəsinə əsasən, dövlət müavinətləri gəlir vergisindən azaddır (Gəlir vergisindən azad: Bəli).
            </p>
          </div>

          <div className={styles.articleItem}>
            <h4>5. Məlumatlandırıcı Hesablama və Rəsmi Qeyd</h4>
            <p>
              Kalkulyatorun əsas hesablanma mexanizmi və təqdim edilmiş test ssenariləri üzrə tətbiqi aktual normativ tələblərlə uyğunlaşdırılmışdır. Kalkulyator məlumatlandırıcı ilkin hesablama vasitəsidir; faktiki müavinətin təyinatı və məbləği DSMF/ƏMAS elektron məlumatları və həmin tarixdə qüvvədə olan qanunvericilik əsasında müəyyən edilir.
            </p>
          </div>
        </div>
      </section>
    </section>
  ) : (
    <Loading />
  )
}

export default Calculator3
